import { FunctionComponent, useMemo } from 'react';
import { Typeahead, Token } from 'react-bootstrap-typeahead';
import { getDataSourceDisplayMemberValue } from './FieldEditorFunctions';

export interface MultiSelectProps {
    /** The name of the field. */
    field: string;
    /** The label for the editor. */
    label?: string;
    /** Is this field required? */
    isRequired?: boolean;
    /** Is the editing enabled? */
    isEnabled?: boolean;
    /** The placeholder for the field value. */
    placeholder?: string;
    /** The datasource for the multi-select. */
    dataSource?: { [index: string]: any }[];
    /** The datasource object's property that is human readable. */
    dataSourceDisplayMember?: string;
    /** The datasource object's property that is used as the real value for the field. */
    dataSourceValueMember?: string;
    /** Is the input invalid? */
    isInvalid?: boolean;
    /** The value of the field. */
    value?: any;
    /** CSS class for the control. */
    className?: string;
    /** Fired when the field value changes in the editor. */
    onValueChanged?: (field: string, value: any) => void;
}

/**
 * Component: react-bootstrap-typeahead-based multiselect with MUI-like behavior.
 */
const MultiSelect: FunctionComponent<MultiSelectProps> = (props) =>
{
    const isEnabled = props.isEnabled ?? true;
    const dataSourceValueMember = props.dataSourceValueMember ?? 'value';
    const dataSourceDisplayMember = props.dataSourceDisplayMember ?? 'display';

    // Raw options
    const rawOptions = (props.dataSource ?? []) as any[];
    const rawById = useMemo(() =>
    {
        const map = new Map<any, any>();
        for (const o of rawOptions)
        {
            if (o && o[dataSourceValueMember] != null) map.set(o[dataSourceValueMember], o);
        }
        return map;
    }, [rawOptions, dataSourceValueMember]);

    const toUiOption = (o: any) => ({
        [dataSourceValueMember]: o?.[dataSourceValueMember],
        __label: String(getDataSourceDisplayMemberValue(o, dataSourceDisplayMember) ?? ''),
    });

    const uiOptions = useMemo(() => rawOptions.map(toUiOption), [rawOptions, dataSourceValueMember, dataSourceDisplayMember]);

    // Sanitize selected values
    const selectedValues = useMemo(() =>
    {
        const arr = (props.value ?? []) as any[];
        const result: any[] = [];
        for (const v of arr)
        {
            const id = typeof v === 'object' ? v?.[dataSourceValueMember] : v;
            const raw = rawById.get(id);
            if (raw) result.push(toUiOption(raw));
            else
            {
                const label = typeof v === 'object'
                    ? String(getDataSourceDisplayMemberValue(v, dataSourceDisplayMember) ?? '')
                    : String(id ?? '');
                result.push({ [dataSourceValueMember]: id, __label: label });
            }
        }
        return result;
    }, [props.value, rawById, dataSourceValueMember, dataSourceDisplayMember]);

    // Optional: limit visible tags to mimic MUI's `limitTags={2}`
    const maxVisibleTags = 2;

    return (
        <Typeahead
            id={`${props.field}-multiselect`}
            multiple
            clearButton
            disabled={!isEnabled}
            isInvalid={props.isInvalid}
            options={uiOptions}
            selected={selectedValues}
            labelKey={(option: any) => String(option?.__label ?? getDataSourceDisplayMemberValue(option, dataSourceDisplayMember))}
            placeholder={props.placeholder}
            className={props.className}
            onChange={(newSelected) =>
            {
                const mapped = (newSelected as any[]).map(ui => rawById.get(ui?.[dataSourceValueMember]) ?? null).filter(x => x != null);
                props.onValueChanged?.(props.field, mapped);
            }}
            // Keep default hint selection behavior
            selectHint={(shouldSelect) => shouldSelect}
            // Render only a couple of tokens like MUI's limitTags
            renderToken={(option, tokenProps, index) =>
            {
                const isObj = typeof option === 'object' && option !== null;
                const keyVal = isObj ? String((option as any)[dataSourceValueMember] ?? index) : String(option);
                const label = isObj
                    ? String((option as any).__label ?? getDataSourceDisplayMemberValue(option as any, dataSourceDisplayMember))
                    : String(option);

                const hidden = index >= maxVisibleTags; // keep types happy by rendering hidden tokens
                const hiddenCount = Math.max(0, selectedValues.length - maxVisibleTags);
                const showPlus = !hidden && index === maxVisibleTags - 1 && hiddenCount > 0;

                return (
                    <>
                        <Token
                            key={keyVal}
                            {...tokenProps}
                            option={option}
                            style={hidden ? { display: 'none' } : undefined}
                        >
                            {label}
                        </Token>
                        {showPlus && (
                            <Token
                                key={`more-${keyVal}`}
                                {...tokenProps}
                                option={option}
                                readOnly
                                onRemove={undefined}
                            >
                                +{hiddenCount}
                            </Token>
                        )}
                    </>
                );
            }}
            // Put validation and xFrame hooks on the actual input element so form validation works
            inputProps={{
                name: props.field,
                required: props.isRequired,
                'aria-label': props.label,
                className: `field-editor-input form-control ${props.isInvalid ? 'is-invalid' : ''}`.trim(),
            }}
            // Ensure equality by value key when the component matches selected vs options
            // (RBT does strict object comparison; providing the objects as selected maintains behavior)
            minLength={0}
        />
    );
}

export default MultiSelect;
